/**
 * CountrySelector Component
 * Flag-based country selector for nationality selection in search bar
 * Uses cookie for anonymous visitors, profile for logged-in users
 */
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { TCKimlikDialog, getTCKimlikStatus, getNationalityGateConfig } from './TCKimlikDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Top countries first, then full list
const TOP_COUNTRIES = ['NL','DE','BE','GB','FR','TR','ES','IT','US','PL','AT','CH','SE','NO','DK'];

const COUNTRIES = [
  {code:'AF',name:'Afghanistan',flag:'🇦🇫'},{code:'AL',name:'Albania',flag:'🇦🇱'},{code:'DZ',name:'Algeria',flag:'🇩🇿'},
  {code:'AD',name:'Andorra',flag:'🇦🇩'},{code:'AO',name:'Angola',flag:'🇦🇴'},{code:'AR',name:'Argentina',flag:'🇦🇷'},
  {code:'AM',name:'Armenia',flag:'🇦🇲'},{code:'AU',name:'Australia',flag:'🇦🇺'},{code:'AT',name:'Austria',flag:'🇦🇹'},
  {code:'AZ',name:'Azerbaijan',flag:'🇦🇿'},{code:'BH',name:'Bahrain',flag:'🇧🇭'},{code:'BD',name:'Bangladesh',flag:'🇧🇩'},
  {code:'BY',name:'Belarus',flag:'🇧🇾'},{code:'BE',name:'Belgium',flag:'🇧🇪'},{code:'BZ',name:'Belize',flag:'🇧🇿'},
  {code:'BA',name:'Bosnia',flag:'🇧🇦'},{code:'BR',name:'Brazil',flag:'🇧🇷'},{code:'BG',name:'Bulgaria',flag:'🇧🇬'},
  {code:'KH',name:'Cambodia',flag:'🇰🇭'},{code:'CA',name:'Canada',flag:'🇨🇦'},{code:'CL',name:'Chile',flag:'🇨🇱'},
  {code:'CN',name:'China',flag:'🇨🇳'},{code:'CO',name:'Colombia',flag:'🇨🇴'},{code:'HR',name:'Croatia',flag:'🇭🇷'},
  {code:'CU',name:'Cuba',flag:'🇨🇺'},{code:'CY',name:'Cyprus',flag:'🇨🇾'},{code:'CZ',name:'Czechia',flag:'🇨🇿'},
  {code:'DK',name:'Denmark',flag:'🇩🇰'},{code:'DO',name:'Dominican Republic',flag:'🇩🇴'},{code:'EC',name:'Ecuador',flag:'🇪🇨'},
  {code:'EG',name:'Egypt',flag:'🇪🇬'},{code:'EE',name:'Estonia',flag:'🇪🇪'},{code:'ET',name:'Ethiopia',flag:'🇪🇹'},
  {code:'FI',name:'Finland',flag:'🇫🇮'},{code:'FR',name:'France',flag:'🇫🇷'},{code:'GE',name:'Georgia',flag:'🇬🇪'},
  {code:'DE',name:'Germany',flag:'🇩🇪'},{code:'GH',name:'Ghana',flag:'🇬🇭'},{code:'GR',name:'Greece',flag:'🇬🇷'},
  {code:'GT',name:'Guatemala',flag:'🇬🇹'},{code:'HN',name:'Honduras',flag:'🇭🇳'},{code:'HK',name:'Hong Kong',flag:'🇭🇰'},
  {code:'HU',name:'Hungary',flag:'🇭🇺'},{code:'IS',name:'Iceland',flag:'🇮🇸'},{code:'IN',name:'India',flag:'🇮🇳'},
  {code:'ID',name:'Indonesia',flag:'🇮🇩'},{code:'IR',name:'Iran',flag:'🇮🇷'},{code:'IQ',name:'Iraq',flag:'🇮🇶'},
  {code:'IE',name:'Ireland',flag:'🇮🇪'},{code:'IL',name:'Israel',flag:'🇮🇱'},{code:'IT',name:'Italy',flag:'🇮🇹'},
  {code:'JM',name:'Jamaica',flag:'🇯🇲'},{code:'JP',name:'Japan',flag:'🇯🇵'},{code:'JO',name:'Jordan',flag:'🇯🇴'},
  {code:'KZ',name:'Kazakhstan',flag:'🇰🇿'},{code:'KE',name:'Kenya',flag:'🇰🇪'},{code:'KW',name:'Kuwait',flag:'🇰🇼'},
  {code:'LV',name:'Latvia',flag:'🇱🇻'},{code:'LB',name:'Lebanon',flag:'🇱🇧'},{code:'LT',name:'Lithuania',flag:'🇱🇹'},
  {code:'LU',name:'Luxembourg',flag:'🇱🇺'},{code:'MY',name:'Malaysia',flag:'🇲🇾'},{code:'MV',name:'Maldives',flag:'🇲🇻'},
  {code:'MT',name:'Malta',flag:'🇲🇹'},{code:'MX',name:'Mexico',flag:'🇲🇽'},{code:'MD',name:'Moldova',flag:'🇲🇩'},
  {code:'MC',name:'Monaco',flag:'🇲🇨'},{code:'ME',name:'Montenegro',flag:'🇲🇪'},{code:'MA',name:'Morocco',flag:'🇲🇦'},
  {code:'NL',name:'Netherlands',flag:'🇳🇱'},{code:'NZ',name:'New Zealand',flag:'🇳🇿'},{code:'NG',name:'Nigeria',flag:'🇳🇬'},
  {code:'MK',name:'North Macedonia',flag:'🇲🇰'},{code:'NO',name:'Norway',flag:'🇳🇴'},{code:'OM',name:'Oman',flag:'🇴🇲'},
  {code:'PK',name:'Pakistan',flag:'🇵🇰'},{code:'PA',name:'Panama',flag:'🇵🇦'},{code:'PY',name:'Paraguay',flag:'🇵🇾'},
  {code:'PE',name:'Peru',flag:'🇵🇪'},{code:'PH',name:'Philippines',flag:'🇵🇭'},{code:'PL',name:'Poland',flag:'🇵🇱'},
  {code:'PT',name:'Portugal',flag:'🇵🇹'},{code:'QA',name:'Qatar',flag:'🇶🇦'},{code:'RO',name:'Romania',flag:'🇷🇴'},
  {code:'RU',name:'Russia',flag:'🇷🇺'},{code:'SA',name:'Saudi Arabia',flag:'🇸🇦'},{code:'RS',name:'Serbia',flag:'🇷🇸'},
  {code:'SG',name:'Singapore',flag:'🇸🇬'},{code:'SK',name:'Slovakia',flag:'🇸🇰'},{code:'SI',name:'Slovenia',flag:'🇸🇮'},
  {code:'ZA',name:'South Africa',flag:'🇿🇦'},{code:'KR',name:'South Korea',flag:'🇰🇷'},{code:'ES',name:'Spain',flag:'🇪🇸'},
  {code:'LK',name:'Sri Lanka',flag:'🇱🇰'},{code:'SE',name:'Sweden',flag:'🇸🇪'},{code:'CH',name:'Switzerland',flag:'🇨🇭'},
  {code:'TW',name:'Taiwan',flag:'🇹🇼'},{code:'TZ',name:'Tanzania',flag:'🇹🇿'},{code:'TH',name:'Thailand',flag:'🇹🇭'},
  {code:'TN',name:'Tunisia',flag:'🇹🇳'},{code:'TR',name:'Turkey',flag:'🇹🇷'},{code:'UA',name:'Ukraine',flag:'🇺🇦'},
  {code:'AE',name:'UAE',flag:'🇦🇪'},{code:'GB',name:'United Kingdom',flag:'🇬🇧'},{code:'US',name:'United States',flag:'🇺🇸'},
  {code:'UY',name:'Uruguay',flag:'🇺🇾'},{code:'UZ',name:'Uzbekistan',flag:'🇺🇿'},{code:'VE',name:'Venezuela',flag:'🇻🇪'},
  {code:'VN',name:'Vietnam',flag:'🇻🇳'}
];

const COUNTRY_MAP = Object.fromEntries(COUNTRIES.map(c => [c.code, c]));

// Cookie helpers
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};
const setCookie = (name, value, days = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
};

export const getCustomerCountry = () => {
  return getCookie('fs_country') || 'NL';
};

export const CountrySelector = ({ compact = false, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('NL');
  const [gateConfig, setGateConfig] = useState(null);
  const [showGateDialog, setShowGateDialog] = useState(false);
  const ref = useRef(null);
  const prevSelected = useRef('NL');

  // Load gate config once
  useEffect(() => {
    getNationalityGateConfig().then(setGateConfig).catch(() => {});
  }, []);

  // Helper: decide whether to show dialog for the given country code
  const maybeTriggerGate = (code) => {
    if (!gateConfig?.enabled) return;
    if (code !== gateConfig.trigger_country) return;
    const status = getTCKimlikStatus();
    if (status === 'yes' || status === 'no') return;
    setShowGateDialog(true);
  };

  // Init from cookie or IP detection
  useEffect(() => {
    const saved = getCookie('fs_country');
    if (saved && COUNTRY_MAP[saved]) {
      setSelected(saved);
      prevSelected.current = saved;
      maybeTriggerGate(saved);
    } else {
      // Auto-detect via IP
      fetch(`${API}/geo/detect-country`)
        .then(r => r.json())
        .then(data => {
          const code = data.country_code || 'NL';
          if (COUNTRY_MAP[code]) {
            setSelected(code);
            setCookie('fs_country', code);
            prevSelected.current = code;
            onChange?.(code);
            maybeTriggerGate(code);
          }
        })
        .catch(() => {});
    }
  }, [gateConfig]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (code) => {
    setSelected(code);
    setCookie('fs_country', code);
    setOpen(false);
    setSearch('');
    onChange?.(code);
    // Reset TC status if user toggles AWAY from trigger_country and back (per spec)
    if (gateConfig?.enabled && prevSelected.current !== code) {
      if (prevSelected.current === gateConfig.trigger_country && code !== gateConfig.trigger_country) {
        // moved AWAY → clear status so gate re-asks when they come back
        import('./TCKimlikDialog').then(m => m.clearTCKimlikStatus());
      }
    }
    prevSelected.current = code;
    maybeTriggerGate(code);
  };

  const country = COUNTRY_MAP[selected] || COUNTRY_MAP['NL'];

  // Filter countries
  const query = search.toLowerCase();
  const filtered = query
    ? COUNTRIES.filter(c => c.name.toLowerCase().includes(query) || c.code.toLowerCase().includes(query))
    : [
        ...TOP_COUNTRIES.map(c => COUNTRY_MAP[c]).filter(Boolean),
        { divider: true },
        ...COUNTRIES.filter(c => !TOP_COUNTRIES.includes(c.code))
      ];

  return (
    <div ref={ref} className="relative" data-testid="country-selector">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 rounded-lg transition-colors hover:bg-white/10 ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
        data-testid="country-selector-trigger"
        title="Select your nationality"
      >
        <span className={compact ? 'text-base' : 'text-lg'}>{country.flag}</span>
        <span className="font-medium text-white/90">{selected}</span>
        <ChevronDown className={`text-white/50 ${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-[#0a2540] border border-[#1e3a5f] rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="country-dropdown">
          <div className="p-2 border-b border-[#1e3a5f]">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-[#081D33] border border-[#1e3a5f] rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-[#00A9E0]"
                autoFocus
                data-testid="country-search-input"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((item, i) => {
              if (item.divider) return <div key="div" className="border-t border-[#1e3a5f] my-1" />;
              return (
                <button
                  key={item.code}
                  onClick={() => handleSelect(item.code)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-[#1e3a5f] ${selected === item.code ? 'bg-[#1e3a5f] text-[#00A9E0]' : 'text-slate-300'}`}
                  data-testid={`country-option-${item.code}`}
                >
                  <span className="text-lg">{item.flag}</span>
                  <span className="flex-1 text-left">{item.name}</span>
                  <span className="text-xs text-slate-500">{item.code}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <TCKimlikDialog
        open={showGateDialog}
        onOpenChange={setShowGateDialog}
        config={gateConfig}
      />
    </div>
  );
};

export default CountrySelector;
